<template>
  <div class="sidebar-container bg-f5f5f5 flex align-items-center flex-shrink-0 relative">
    <!-- 左侧一级菜单 -->
    <div class="sidebar-left w-68 flex flex-col items-center">
      <div class="first-menu-container">
        <el-menu
          :default-active="activeMenu"
          class="sidebar-menu"
          @select="handleMenuSelect"
        >
          <el-menu-item
            v-for="menu in firstLevelMenusWithChildren"
            :key="menu.id"
            :index="menu.id"
            :class="{ active: activeFirstMenu === menu.id }"
            @mouseenter.native="handleMenuHover(menu.id)"
            @mouseleave.native="handleMenuLeave"
          >
            <img :src="getMenuIcon(menu.icon)" class="w-20 h-20">
            <span slot="title">{{ menu.component }}</span>
          </el-menu-item>
        </el-menu>
      </div>
    </div>
    
    <!-- 右侧二级菜单 -->
    <div v-show="!isCollapsed" class="flex flex-col flex-1 bg-white w-180 px-20 box-sizing border-radius-4">
      <!-- 标题区域 -->
      <div class="menu-header pt-24 pb-21">
        <div class="flex items-center gap-8 text-16 font-bold lh-20">
          <img :src="getActiveMenuIcon()" class="w-20 h-20">
          <span>{{ getActiveMenuName() }}</span>
        </div>
        <div class="collapse-btn w-20 h-20 bg-white absolute text-center cursor-pointer" @click="toggleSidebar">
          <i :class="isCollapsed ? 'el-icon-d-arrow-left' : 'el-icon-d-arrow-right'" style="line-height: 20px;"></i>
        </div>
      </div>
      <!-- 二级菜单 -->
      <div class="second-menu-container">
        <el-menu
          :default-active="activeSubMenu"
          class="submenu pt-12 bg-white"
          @select="handleSubMenuSelect"
        >
          <el-menu-item
            v-for="submenu in visibleSecondLevelMenus"
            :key="submenu.id"
            :index="submenu.id"
            :class="{ 'is-active': activeSubMenu === submenu.id }"
          >
            <img :src="getMenuIcon(submenu.icon)" class="w-14 h-14"/>
            <span class="ml-8" slot="title">{{ submenu.name }}</span>
          </el-menu-item>
        </el-menu>
      </div>
    </div>
    
    <!-- 折叠状态下的按钮 -->
    <div v-show="isCollapsed" class="flex items-center justify-center w-20 h-20 bg-white cursor-pointer absolute collapse-right-btn" @click="toggleSidebar">
      <i class="el-icon-d-arrow-right" style="line-height: 20px;"></i>
    </div>
    
    <!-- 折叠状态下的弹出菜单 -->
    <div 
      v-show="shouldShowPopup" 
      class="popup-submenu"
      :style="{ top: getPopupTop() + 'px' }"
      @mouseenter="handlePopupHover"
      @mouseleave="handlePopupLeave"
    >
      <div class="popup-submenu-list p-16">
        <div 
          v-for="submenu in getHoveredSubMenus()"
          :key="submenu.id"
          :class="[
            'popup-submenu-item h-42 flex items-center gap-8 p-12 border-radius-4 cursor-pointer',
            { 'popup-submenu-item-active': submenu.id === activeMenu }
          ]"
          @click="handleSubMenuSelect(submenu.id)"
        >
          <img :src="getMenuIcon(submenu.icon)" class="w-14 h-14">
          <span class="popup-item-text text-14">{{ submenu.name }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppSidebar',
  props: {
    firstLevelMenus: {
      type: Array,
      default: () => []
    },
    secondLevelMenus: {
      type: Array,
      default: () => []
    },
    activeMenu: {
      type: String,
      default: ''
    },
    activeFirstMenu: {
      type: String,
      default: ''
    },
    activeSubMenu: {
      type: String,
      default: ''
    },
    isCollapsed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hoveredMenuId: null,
      hoverTimeout: null
    }
  },
  computed: {
    // 过滤出有子菜单的一级菜单
    firstLevelMenusWithChildren() {
      return this.firstLevelMenus.filter(menu => {
        // 如果没有子菜单，不显示
        if (!menu.children || menu.children.length === 0) {
          return false
        }
        
        // 过滤掉隐藏的菜单
        const visibleChildren = menu.children.filter(child => 
          !child.hidden
        )
        
        // 如果过滤后没有可见的子菜单，则不显示一级菜单
        return visibleChildren.length > 0
      })
    },
    // 过滤出可见的二级菜单（排除hidden的菜单）
    visibleSecondLevelMenus() {
      return this.secondLevelMenus.filter(menu => !menu.hidden)
    },
    shouldShowPopup() {
      return this.isCollapsed && this.hoveredMenuId && this.getHoveredSubMenus().length > 0
    }
  },
  methods: {
    handleMenuSelect(menuId) {
      // 如果是折叠状态，点击一级菜单时也设置悬停状态，以便显示弹出菜单
      if (this.isCollapsed) {
        this.hoveredMenuId = menuId
      }
      this.$emit('menu-select', menuId)
    },
    handleSubMenuSelect(submenuId) {
      // 如果是折叠状态下的弹出菜单点击，需要先设置当前一级菜单和二级菜单
      if (this.isCollapsed && this.hoveredMenuId) {
        // 先触发一级菜单选择事件，确保父组件设置正确的二级菜单
        this.$emit('menu-select', this.hoveredMenuId, true) // 传递 fromPopup = true
        // 等待下一个tick确保父组件已经更新了secondLevelMenus
        this.$nextTick(() => {
          this.$emit('submenu-select', submenuId)
          // 点击二级菜单后隐藏弹出菜单
          this.hoveredMenuId = null
        })
      } else {
        this.$emit('submenu-select', submenuId)
        // 点击二级菜单后隐藏弹出菜单
        this.hoveredMenuId = null
      }
    },
    getMenuIcon(icon) {
      
      // 如果是完整的URL，直接返回
      if (/^https?:\/\//.test(icon)) return icon
      
      // 如果是本地资源路径（包含@/assets或require），直接返回
      if (icon.includes('@/assets') || icon.includes('require')) return icon
      
      // 本地图标数组，用于匹配
      const localIcons = [
        { label: 'setting', value: require('../../assets/setting.png') },
        { label: 'purchase_address', value: require('../../assets/purchase_address.png') },
        { label: 'ticket_management', value: require('../../assets/ticket_management.png') },
        { label: 'system_parameters', value: require('../../assets/system_parameters.png') },
        { label: 'announcement_management', value: require('../../assets/announcement_management.png') },
        { label: 'help', value: require('../../assets/help.png') },
        { label: 'position_authorization', value: require('../../assets/position_authorization.png') },
        { label: 'data_archive', value: require('../../assets/data_archive.png') },
        { label: 'org', value: require('../../assets/org.png') },
        { label: 'warehouse', value: require('../../assets/warehouse.png') },
        { label: 'blacklist', value: require('../../assets/blacklist.png') },
        { label: 'vehicle_info', value: require('../../assets/vehicle_info.png') },
        { label: 'driver_info', value: require('../../assets/driver_Info.png') },
        { label: 'driver_review', value: require('../../assets/driver_review.png') },
        { label: 'ticket_processing', value: require('../../assets/ticket_processing.png') },
        { label: 'product', value: require('../../assets/product.png') },
        { label: 'operation', value: require('../../assets/operation.png') },
        { label: 'add_order', value: require('../../assets/add_order.png') },
        { label: 'order', value: require('../../assets/order.png') },
        { label: 'driver', value: require('../../assets/driver.png') }
      ]
      
      // 尝试匹配本地图标
      // 1. 如果icon是文件名（如 system_params.png），提取文件名进行匹配
      const iconFileName = icon.split('/').pop().replace('.png', '')
      const matchedIcon = localIcons.find(item => item.label === iconFileName)
      if (matchedIcon) {
        return matchedIcon.value
      }
      
      // 2. 如果icon包含完整路径（如 assets/system_params.png），也尝试匹配
      const iconPath = icon.replace(/^\/?assets\//, '').replace('.png', '')
      const matchedIconByPath = localIcons.find(item => item.label === iconPath)
      if (matchedIconByPath) {
        return matchedIconByPath.value
      }
      
      // 如果没有匹配到本地图标，兜底使用一个默认本地图标（本地）
      return require('../../assets/setting.png')
    },
    getActiveMenuIcon() {
      const activeMenu = this.firstLevelMenus.find(menu => menu.id === this.activeFirstMenu)
      return activeMenu ? this.getMenuIcon(activeMenu.icon) : require('../../assets/setting.png')
    },
    getActiveMenuName() {
      const activeMenu = this.firstLevelMenus.find(menu => menu.id === this.activeFirstMenu)
      return activeMenu ? activeMenu.name : '菜单'
    },
    toggleSidebar() {
      console.log('toggleSidebar called, current isCollapsed:', this.isCollapsed)
      this.$emit('toggle-sidebar', !this.isCollapsed)
    },
    // 处理一级菜单悬停
    handleMenuHover(menuId) {
      if (!this.isCollapsed) return
      
      // 清除之前的定时器
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
      }
      
      // 如果已经是同一个菜单，不需要重新设置
      if (this.hoveredMenuId === menuId) return
      
      this.hoveredMenuId = menuId
    },
    // 处理一级菜单离开
    handleMenuLeave() {
      if (!this.isCollapsed) return
      
      // 延迟隐藏，避免鼠标移动到弹出菜单时立即消失
      this.hoverTimeout = setTimeout(() => {
        this.hoveredMenuId = null
      }, 200)
    },
    // 处理弹出菜单悬停
    handlePopupHover() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
      }
    },
    // 处理弹出菜单离开
    handlePopupLeave() {
      // 延迟隐藏，避免鼠标移动到一级菜单时立即消失
      this.hoverTimeout = setTimeout(() => {
        this.hoveredMenuId = null
      }, 200)
    },
    // 获取当前悬停菜单的二级菜单
    getHoveredSubMenus() {
      if (!this.hoveredMenuId) return []
      const menu = this.firstLevelMenus.find(m => m.id === this.hoveredMenuId)
      return menu ? (menu.children || []) : []
    },
    // 计算弹出菜单的顶部位置
    getPopupTop() {
      if (!this.hoveredMenuId) return 0
      const menuIndex = this.firstLevelMenus.findIndex(m => m.id === this.hoveredMenuId)
      // 每个菜单项高度约52px，加上间距，计算大概位置
      return 20 + menuIndex * 66 // 20px是容器顶部padding，66px是每个菜单项的高度+间距
    }
  }
}
</script>

<style scoped>
.sidebar-menu {
  border: none;
  background: transparent;
}

.sidebar-menu .el-menu-item {
  height: 52px;
  width: 52px;
  line-height: 18px;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: none;
  color: rgba(48, 49, 51, 1);
  border-radius: 8px;
  margin: 28px 8px 0 8px !important;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}
.sidebar-menu .el-menu-item span{
  margin-top: 4px;
  display: inline-block;
}
.sidebar-menu .el-menu-item i{
  display: block;
  width: 19px;
  height: 20px;
  margin: 0px !important
}
.sidebar-menu .el-menu-item:hover {
  background: rgba(237, 237, 237, 1);
  border-radius: 8px;
}

.sidebar-menu .el-menu-item.active {
  background:rgba(42, 117, 247, 0.08);
  color: rgba(30, 105, 235, 1);
}
/* 二级菜单项悬停效果 */
.submenu .el-menu-item:hover {
  background: rgba(42, 117, 247, 0.08);
  color: rgba(42, 117, 247, 1);
  border-radius: 6px;
  cursor: pointer;
}

/* 二级菜单项悬停时图片变蓝色 */
.submenu .el-menu-item:hover img {
  filter: brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(97%) contrast(96%);
}

.submenu .el-menu-item.is-active {
  background: rgba(42, 117, 247, 0.08);
  color: rgba(42, 117, 247, 1);
  border-radius: 6px;
}

/* 二级菜单项激活状态时图片变蓝色 */
.submenu .el-menu-item.is-active img {
  filter: brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(97%) contrast(96%);
}
.menu-header{
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.collapse-btn {
  z-index: 2000;
  border-radius: 50%;
  right: -4px;
  top: 10px;
  opacity: 1;
  transform: rotate(180deg);
  box-shadow: 0px 1px 4px  rgba(30, 105, 235, 0.12);
  transition: transform .16s ease, box-shadow .2s ease, background-color .2s ease;

}
 .collapse-btn i, .collapse-right-btn i{
   color: rgba(144, 147, 153, 1);
   font-size: 12px;
 }
 .collapse-btn:hover i, .collapse-right-btn:hover i {
   color: rgba(42, 117, 247, 1);
 }
 .collapse-btn:hover{
   transform: rotate(180deg) scale(1.15);
   box-shadow: 0 8px 24px rgba(42,117,247,0.18);
   background: rgba(245,247,249,1);
 }
 .collapse-right-btn{
  top: 10px;
  right: -18px;
  border-radius: 50%;
  transition: transform .16s ease, box-shadow .2s ease, background-color .2s ease;
  z-index: 9999;
  box-shadow: 0px 1px 4px rgba(30, 105, 235, 0.12);
 }
 .collapse-right-btn:hover{
   transform: scale(1.15);
   box-shadow: 0 8px 24px rgba(42,117,247,0.18);
   background: rgba(245,247,249,1);
 }
 /* 折叠状态下的按钮样式 */
 .sidebar-container > div:last-child {
   margin-left: 8px;
   box-shadow: 0px 1px 4px rgba(30, 105, 235, 0.12);
 }
 
 .sidebar-container > div:last-child:hover {
   background: #f5f5f5;
 }
.submenu{
  border: none;
}
.submenu .el-menu-item {
  height: 42px;
  line-height: 42px;
  margin-bottom: 4px;
  padding: 0 20px;
  font-size: 14px;
  color: #666;
  border: none;
  background: white;
  border-radius: 6px;
  overflow: hidden;
}

/* 一级菜单滚动容器 */
.first-menu-container {
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0px 0 20px 0;
  width: 100%;
}
/* 二级菜单滚动容器 */
.second-menu-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - 120px);
}
.second-menu-container::-webkit-scrollbar,.first-menu-container::-webkit-scrollbar{
  width: 4px !important;
}
/* 确保菜单项在滚动时保持正确间距 */
.sidebar-menu .el-menu-item:first-child {
  margin-top: 0;
}
.sidebar-menu .el-menu-item:last-child {
  margin-bottom: 20px;
}

/* 滚动时的平滑效果 */
.first-menu-container,
.second-menu-container {
  scroll-behavior: smooth;
}

/* 滚动指示器 */
.first-menu-container::before,
.second-menu-container::before {
  content: '';
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.1), transparent);
  z-index: 1;
}

/* 滚动到底部时的指示器 */
.first-menu-container::after,
.second-menu-container::after {
  content: '';
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.1), transparent);
  z-index: 1;
}

/* 弹出式二级菜单样式 */
.popup-submenu {
  position: absolute;
  left: 68px;
  top: 0;
  width: 200px;
  background: white !important;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  overflow: hidden;
  animation: slideInRight 0.2s ease-out;
  border: 1px solid rgba(228, 231, 237, 1);
}

/* 确保弹出菜单容器背景色不变 */
.popup-submenu:hover {
  background: white !important;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.popup-submenu-list {
  max-height: 600px;
  overflow-y: auto;
  background: white !important;
}

.popup-submenu-list:hover {
  background: white !important;
}

.popup-submenu-item:hover {
  background: rgba(42, 117, 247, 0.08);
}

.popup-submenu-item:hover .popup-item-text {
  color: rgba(42, 117, 247, 1);
}

.popup-submenu-item:hover img {
  filter: brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(97%) contrast(96%);
}

/* 弹出菜单项选中状态 */
.popup-submenu-item-active {
  background: rgba(42, 117, 247, 0.08);
  color: rgba(42, 117, 247, 1);
}

.popup-submenu-item-active .popup-item-text {
  color: rgba(42, 117, 247, 1);
}

.popup-submenu-item-active img {
  filter: brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(97%) contrast(96%);
}

.popup-item-text {
  color: rgba(96, 98, 102, 1)
}

</style>